import type { Answer, Outcome, Question, WizardState } from './types';

export const initialState: WizardState = {
    currentIndex: 0,
    answers: {},
    result: null,
};

/**
 * Process a user's answer to the current question.
 * - Circuit-breaker + "ja" → sets result immediately.
 * - Circuit-breaker + "nei" → advances to next question, answer not stored.
 * - Accumulated question → stores answer, advances; if last question, calculates result.
 */
export function processAnswer(questions: Question[], state: WizardState, answer: Answer): WizardState {
    const question = questions[state.currentIndex];
    const isLast = state.currentIndex === questions.length - 1;

    if (question.isCircuitBreaker) {
        if (answer === 'ja') {
            return {
                ...state,
                answers: { ...state.answers, [question.id]: answer },
                result: question.yesOutcome,
            };
        }
        // nei on circuit-breaker: advance without triggering outcome, but store the answer
        return { ...state, answers: { ...state.answers, [question.id]: answer }, currentIndex: state.currentIndex + 1 };
    }

    // Accumulated question
    const updatedAnswers = { ...state.answers, [question.id]: answer };

    if (isLast) {
        return {
            ...state,
            answers: updatedAnswers,
            result: calculateResult(questions, updatedAnswers),
        };
    }

    return { ...state, answers: updatedAnswers, currentIndex: state.currentIndex + 1 };
}

/**
 * Calculate the final outcome from accumulated answers using majority vote.
 * Tie goes to "oppfølging" (default).
 */
export function calculateResult(questions: Question[], answers: Record<string, Answer>): Outcome {
    let arbeidssøkerScore = 0;
    let oppfølgingScore = 0;

    for (const question of questions) {
        if (question.isCircuitBreaker) continue;
        const answer = answers[question.id];
        if (!answer) continue;

        const outcome = answer === 'ja' ? question.yesOutcome : question.noOutcome;
        if (outcome === 'arbeidssøker') {
            arbeidssøkerScore++;
        } else {
            oppfølgingScore++;
        }
    }

    return arbeidssøkerScore > oppfølgingScore ? 'arbeidssøker' : 'oppfølging';
}

/** Returns true when the wizard has a final result. */
export function isComplete(state: WizardState): boolean {
    return state.result !== null;
}

/** Returns true when the user can navigate back. */
export function canGoBack(state: WizardState): boolean {
    return state.result !== null || state.currentIndex > 0;
}
